/* ══════════════════════════════════════════════════════════════════════════
   data.js — client constants + Soft educational copy that still has a home
   in the CMC shell. Live registers (devices, zones, people, OTA, audit,
   ONCR catalogues) come from the API — not from this file.
   ══════════════════════════════════════════════════════════════════════════ */

export const CENTRE = [101.4076, 3.0418];

/* Soft-only educational examples (not claimed as live incident data).
   Runtime registries (scorers / intents / autonomy) come from
   GET /ai/oncr/catalogue. Soft-lanes come from GET /ai/soft-lanes. */
export const SIGNAL_FAMILIES = ["battery","rssi","ack","snapshot","offline","repeat"];

export const GROUNDING = {
  observed:["repeat","rssi","ack"],
  model_said:"Repeated motion at Cam 04 with a weak radio link and no acknowledgement. The unit is also running low on battery and has been offline intermittently.",
  flagged:["battery","offline"],
  grounded:true,
  substituted:"Three triggers in 15 minutes at Cam 04, radio at −72 dBm, and no acknowledgement for 742 seconds.",
};

/* Soft backlog constants for Intel Gaps Soft≠Live desk — labeled Soft only.
   Live inventory is GET /ai/oncr/catalogue + GET /ai/soft-lanes. Never paint
   these rows as Live counters. */
export const AI_GAPS = [
  { id:"O7", name:"Policy evolution", file:"policy evolution",
    say:"Full draft → submit → SA review lifecycle. Never persisted, and nothing calls it.", sev:"dead" },
  { id:"O9c", name:"Crown backfill", file:"crown backfill",
    say:"Complete and dry-run capable. Nothing schedules it and nothing calls it — pre-ONCR incidents keep the legacy crown forever.", sev:"dead" },
  { id:"O5", name:"Safety Kernel taxonomy", file:"the intent runtime",
    say:"Exposed read-only via GET /ai/oncr/catalogue.", sev:"live" },
  { id:"O4", name:"Policy Graph catalog", file:"policy search",
    say:"Weights + registry via GET /ai/oncr/catalogue; per-incident candidates still on ONCR view.", sev:"live" },
  { id:"O2", name:"Hypothesis mutation", file:"the hypothesis stage",
    say:"Belief state is machine-write-only. A human cannot confirm or refute.", sev:"hidden" },
  { id:"O8", name:"Canary arming", file:"the shadow canary",
    say:"No UI to arm, inspect or audit a tenant into the allowlist. Env-only.", sev:"hidden" },
  { id:"O1", name:"Non-alarm adapters", file:"the observation contract",
    say:"Camera, GPS and operator-note adapters are written and exported but never called.", sev:"hidden" },
  { id:"—", name:"AI decision observability", file:"incident observability",
    say:"Quality tab reads Live AI decision metrics when the ops dashboard seat is open. Soft fixture histograms stay forbidden.", sev:"live" },
];

export const SA_CAPS = [
  { id:"sa_full_access",   label:"Full access",   wild:true, say:"Wildcard. Satisfies every check and bypasses tenant fairness." },
  { id:"sa_ai_admin",      label:"AI admin",      say:"Publish platform models, BYOK ceiling, skills catalogue and MCP tool servers." },
  { id:"sa_device_admin",  label:"Device admin",  say:"Custody transfer, group shares, global device visibility, claim and provision." },
  { id:"sa_ota_admin",     label:"OTA admin",     say:"Create campaigns from URL, stored artifact or upload." },
  { id:"sa_security_admin",label:"Security admin",say:"Rotate cmd keys, read MAC drift, run all four backup routes." },
  { id:"sa_user_admin",    label:"User admin",    say:"List admins and close tenants." },
  { id:"sa_billing_admin", label:"Billing admin", say:"Defined in code — no route enforces it today.", unused:true },
  { id:"sa_audit_read",    label:"Audit read",    say:"Defined in code — no route enforces it today.", unused:true },
];

/* Soft placeholder for Soft≠Live rehearsal panes only — never paint as Live identity. */
export const USER = {
  name:"Soft operator", username:"soft.operator", email:"soft.operator@example.invalid",
  role:"admin", zones:["*"], tenant:"soft.operator", initials:"SO",
  caps:[], session_expires:"—", sv:0,
};

/* the security layer–164 — applies to signup, both resets, admin-create, self-change */
export const PW_RULES = [
  { id:"too_short",        say:"At least 12 characters", test:v=>v.length>=12 },
  { id:"missing_uppercase",say:"One uppercase letter",   test:v=>/[A-Z]/.test(v) },
  { id:"missing_lowercase",say:"One lowercase letter",   test:v=>/[a-z]/.test(v) },
  { id:"missing_digit",    say:"One digit",              test:v=>/[0-9]/.test(v) },
  { id:"missing_symbol",   say:"One symbol",             test:v=>/[^A-Za-z0-9\s]/.test(v) },
  { id:"all_digits",       say:"Not all digits",         test:v=>!/^\d+$/.test(v||"x") },
  { id:"too_common",       say:"Not a known weak password",
    test:v=>!["admin","password123","qwerty","letmein","welcome1","password1"].includes((v||"").toLowerCase()) },
];

/* Model routing for Forge / node3d: live /ai/models preference by purpose
   (see models-api.js JOB_PURPOSE). Soft MODEL_JOBS / MODEL_PROVIDERS removed. */

/** SETTINGS-UX L2 group order (SoT §3.6 / SURF-SET-L2) — never name a group "Platform". */
export const SETTINGS_L2_GROUPS = Object.freeze([
  "Account & Access",
  "Notifications & Alarms",
  "AI Control",
  "Integrations",
  "Governance & Risk",
]);

export const SETTINGS_NAV = [
  { id:"profile",   label:"Profile",       group:"Account & Access" },
  { id:"security",  label:"Password",      group:"Account & Access" },
  { id:"sessions",  label:"Sessions",      group:"Account & Access" },
  { id:"delegations", label:"Delegations", group:"Account & Access" },
  { id:"users", label:"People & access", group:"Account & Access" },
  { id:"notify",    label:"Notifications & delivery", group:"Notifications & Alarms" },
  { id:"alarm",     label:"Alarm behaviour",group:"Notifications & Alarms" },
  { id:"trigger",   label:"Trigger policy",group:"Notifications & Alarms" },
  { id:"ai",        label:"AI team tools",  group:"AI Control" },
  { id:"integrate", label:"Telegram & voice", group:"Integrations", gate:"admin" },
  { id:"audit",     label:"Audit export",  group:"Governance & Risk", gate:"admin" },
  { id:"billing",   label:"Billing",       group:"Governance & Risk", gate:"admin" },
  /* SET-1 / SETTINGS-UX: OTA moved to OTA primary — not a Settings pane. */
  { id:"danger",    label:"Danger zone",   group:"Governance & Risk" },
];

/** Settings side-rail filter — CAP-COMPLETE: exact seats, never caps.length. */
export function settingsNavFor(me){
  const role = String((me && me.role) || "");
  const caps = Array.isArray(me && me.caps) ? me.caps : [];
  const EXACT = new Set([
    "sa_full_access", "sa_billing_admin", "sa_device_admin", "sa_ota_admin",
    "sa_audit_read", "sa_security_admin", "sa_user_admin", "sa_ai_admin",
  ]);
  const isSa = role === "superadmin" || caps.some((c) => EXACT.has(String(c || "").trim()));
  const isAdmin = isSa || role === "admin";
  return SETTINGS_NAV.filter(s => {
    if(!s.gate) return true;
    if(s.gate === "sa") return isSa;
    if(s.gate === "admin") return isAdmin;
    return true;
  });
}

/* Default drafts when the group-card / trigger-policy GET returns empty. */
export const GROUP_CARD = {
  trigger_mode:"delay",
  trigger_duration_ms:8000,
  delay_seconds:15,
  reboot_self_check:false,
};

export const TRIGGER_POLICY = {
  panic_local_siren:false,
  panic_link_enabled:true,
  panic_fanout_duration_ms:20000,
  remote_silent_link_enabled:true,
  remote_loud_link_enabled:true,
  remote_loud_duration_ms:8000,
  fanout_exclude_self:true,
};

export const AI_MODES = ["rules","fast","hybrid","deep","orchestrator","both"];

export const SYSTEM_STATES = [
  { id:"boot",    code:"—",   title:"Bringing the console up",
    say:"The console is awake and the system behind it is still coming to readiness. Nothing is wrong; this screen simply refuses to show you numbers it cannot stand behind yet.",
    mean:"Your site did not stop. Devices kept watching and kept recording while this was loading.",
    art:"boot", tone:"ai" },

  { id:"400",     code:"400", title:"That request never made sense",
    say:"Something in what was asked was malformed before it reached anything of yours. It was refused on sight rather than half-understood and acted on.",
    mean:"Nothing was read and nothing was changed. Reload the screen and try once more.",
    art:"malform", tone:"warn" },

  { id:"401",     code:"401", title:"You have been signed out everywhere",
    say:"Your account changed — a role, a password, a status, or the site you belong to. Every device you were signed in on ended at the same moment, on purpose.",
    mean:"Nothing at your site stopped. Sign in again and you will pick it up exactly where it is.",
    art:"sever", tone:"bad" },

  { id:"403",     code:"403", title:"Your role stops here",
    say:"You are signed in and this needs something your account does not carry. It was not shown to you and nothing was held against you for asking.",
    mean:"Someone with a wider role can open this, or it can be added to yours.",
    art:"gate", tone:"warn" },

  { id:"404",     code:"404", title:"Nothing lives at this address",
    say:"The address is well formed and there is nothing behind it. It may have been closed, handed on, or never created at all.",
    mean:"If you followed this from inside the console, the thing it pointed at is gone.",
    art:"sweep", tone:"bad" },

  { id:"409",     code:"409", title:"Someone else changed this first",
    say:"Your change reached a newer revision. Nothing was overwritten. Re-read the latest state, then try again if you still need the edit.",
    mean:"Two writers met. One kept the new version; yours was refused on purpose.",
    art:"fracture", tone:"warn" },

  { id:"429",     code:"429", title:"Too many attempts, too quickly",
    say:"This is a brake, not a fault. It is deliberate, it gets longer each time it trips, and it lets go on its own without anyone intervening.",
    mean:"The countdown below is the real wait. Nothing else about your account has changed.",
    art:"meter", tone:"warn" },

  { id:"500",     code:"500", title:"The work stopped halfway and was undone",
    say:"What you asked for was understood, started, and failed partway through. Everything it had begun was put back the way it was before you asked.",
    mean:"Nothing is half-written. What you were changing is still in its previous state.",
    art:"fracture", tone:"bad" },

  { id:"502",     code:"502", title:"The console cannot reach the system",
    say:"The front door answered and what stands behind it did not. Your session is intact and nothing of yours has been touched.",
    mean:"Your devices keep running and keep recording. You are the one who is cut off, not your site.",
    art:"relay", tone:"bad" },

  { id:"503",     code:"503", title:"The system is turning work away",
    say:"It is alive and it is deliberately declining to take on more — moving between stores, restarting, or shedding load to stay standing.",
    mean:"Looking is likely to work. Anything you try to change will be refused until this clears.",
    art:"shed", tone:"warn" },

  { id:"offline", code:"—",   title:"You went offline",
    say:"The live feed dropped. Everything you can see below is the last state that reached this browser, frozen at the moment shown.",
    mean:"This is the past, not the present. Do not act on it as though it were live.",
    art:"tether", tone:"warn" },
];
